/**
 * DUPLICATES — and being able to do something about them.
 *
 * The old version of this screen found the same customer entered twice and
 * then said so in its own last card: "Merging is not built here yet." It
 * was a page that told you about a problem and handed you nothing, which
 * over a few weeks is worse than no page at all — you learn to scroll past
 * it.
 *
 * Three tabs, because there are three different problems wearing one word:
 *
 *   PEOPLE   The same person entered twice, splitting their history. One
 *            copy has the jobs, the other has the conversation. Merging is
 *            a single database transaction across seven tables.
 *
 *   QUOTES   An empty estimate sitting beside the real one. Almost always
 *            a lead that converted twice.
 *
 *   JOBS     The same, without the status filter.
 *
 * ─── THE RULE THIS SCREEN IS BUILT ON ──────────────────────────────────
 *
 * IT SHOWS ITS WORKING AND IT NEVER DECIDES. Every row carries the reasons
 * it is on the list and a count of what each record holds, because the one
 * thing that would make this page dangerous is somebody trusting it. Two
 * people really do share a household number, and this shop's data has a
 * pair — Mario Pulido and Bertha Ramos — where they do.
 *
 * The verbs live in the database (`merge_customers`, `merge_vehicles`,
 * `retire_duplicate_job`). Not because it is tidier: combining two
 * customers moves jobs, vehicles, invoices, photos, conversations, leads
 * and tasks, and a dropped connection halfway through seven browser calls
 * leaves a customer whose cars belong to one record and whose invoices
 * belong to another, with no way to tell afterwards which half is which.
 */

import { db } from "../data/client.ts";
import { mount, esc, ago, money, since } from "../ui/dom.ts";
import { denied } from "./admin.ts";
import type { Role } from "../domain/permissions.ts";
import { shopWords } from "../ui/words.ts";

const BOSS: Role[] = ["admin", "supervisor"];

type Dup = {
  phone10: string; phone: string; customer_id: string; full_name: string;
  email: string | null; created_at: string;
  jobs: number; vehicles: number; invoices: number;
  messages: number; photos: number; money: number;
};

type Pair = {
  score: number; reasons: string[];
  keep_id: string; keep_number: string; keep_status: string;
  keep_title: string | null; keep_total: number; keep_made: string;
  drop_id: string; drop_number: string; drop_status: string;
  drop_title: string | null; drop_total: number; drop_made: string;
  customer: string; hours_apart: number;
};

type Tab = "people" | "quotes" | "jobs";
let tab: Tab = "people";

/** How much of a record there is to lose. Nought means nothing is at stake. */
const weight = (d: Dup) =>
  Number(d.jobs) + Number(d.vehicles) + Number(d.invoices) +
  Number(d.messages) + Number(d.photos) + (Number(d.money) > 0 ? 5 : 0);

export async function duplicatesPage(role: Role = "viewer"): Promise<void> {
  if (!BOSS.includes(role)) return denied("Duplicates");

  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>Duplicates</h1>
      <p>The same person or the same job entered twice. Every row shows why it is
         here and what each copy is holding &mdash; nothing on this page decides
         anything for you.</p></header>
    <div class="listbar">
      <div class="jtabs" id="dtabs">
        <button class="jtab ${tab === "people" ? "on" : ""}" data-dtab="people">People</button>
        <button class="jtab ${tab === "quotes" ? "on" : ""}" data-dtab="quotes">Quotes</button>
        <button class="jtab ${tab === "jobs" ? "on" : ""}" data-dtab="jobs">Jobs</button>
      </div>
    </div>
    <div id="dups"><div class="loading">Reading&hellip;</div></div>
  </div>`);

  document.querySelectorAll<HTMLButtonElement>("[data-dtab]").forEach((b) =>
    b.addEventListener("click", () => {
      tab = (b.dataset["dtab"] ?? "people") as Tab;
      void duplicatesPage(role);
    }));

  const host = document.getElementById("dups");
  if (!host) return;

  try {
    if (tab === "people") await drawPeople(host, role);
    else await drawJobs(host, role, tab === "quotes");
  } catch (e) {
    host.innerHTML = `<div class="card"><div class="empty bad">${
      esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}

/* ── the same person, twice ─────────────────────────────────────────── */

async function drawPeople(host: HTMLElement, role: Role): Promise<void> {
  const sb = await db();
  const { data, error } = await sb.rpc("duplicate_customers");
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Dup[];

  const byPhone = new Map<string, Dup[]>();
  for (const r of rows) byPhone.set(r.phone10, [...(byPhone.get(r.phone10) ?? []), r]);
  const groups = [...byPhone.entries()];

  host.innerHTML = `
    <div class="mtiles">
      <div class="mtile count"><div class="mtile-l">Numbers used twice</div>
        <div class="mtile-v">${groups.length}</div>
        <div class="mtile-n">matched on the phone, because that is the thing
          a person has one of</div></div>
      <div class="mtile count"><div class="mtile-l">Records involved</div>
        <div class="mtile-v">${rows.length}</div>
        <div class="mtile-n">customer records sharing a number with another</div></div>
    </div>

    ${groups.length ? groups.map(([phone, list]) => {
      // The one with the most on it is offered as the keeper, because
      // merging into the emptier record is the same work with more to
      // lose if it goes wrong.
      const ranked = [...list].sort((a, b) => weight(b) - weight(a));
      const keeper = ranked[0]!;
      const names = new Set(list.map((c) => c.full_name.trim().toLowerCase()));
      return `
      <div class="card" data-group="${esc(phone)}">
        <div class="card-head">${esc(list[0]?.phone ?? phone)}
          &middot; ${list.length} records</div>
        ${names.size > 1 ? `<div class="wt-trap">Two different names on this number.
          A household shares a phone &mdash; open both before deciding they are
          one person.</div>` : ""}
        ${ranked.map((c, i) => `
          <div class="duprow ${i === 0 ? "keeper" : ""}">
            <div class="duprow-who">
              <a href="#/customer/${esc(c.customer_id)}"><b>${esc(c.full_name)}</b></a>
              ${i === 0 ? `<span class="pill">most history</span>` : ""}
              <div class="row-sub">${esc(c.email ?? "no email")}
                &middot; added ${esc(since(c.created_at))}</div>
            </div>
            <div class="duprow-has">
              ${holds(c)}
            </div>
            ${i === 0 ? `<div class="duprow-act quiet small">keeping this one</div>`
              : `<div class="duprow-act">
                   <button class="btn ghost sm" data-merge="${esc(c.customer_id)}"
                     data-into="${esc(keeper.customer_id)}"
                     data-name="${esc(c.full_name)}"
                     data-keepname="${esc(keeper.full_name)}">Merge into ${
                       esc(keeper.full_name.split(" ")[0] ?? "the other")}</button>
                 </div>`}
          </div>`).join("")}
        <div class="msg-slot" data-msg="${esc(phone)}"></div>
      </div>`;
    }).join("") : `<div class="card"><div class="empty">
        No two customers share a number. <span>&#127942;</span></div></div>`}

    <div class="card">
      <div class="card-head">What merging actually does</div>
      <div class="wt-trap">It moves the jobs, cars, invoices, conversations, photos,
        leads and tasks onto the record you keep, fills any blank field on the keeper
        from the other one, joins their notes, and deletes the copy. One transaction
        &mdash; it either all moves or none of it does.</div>
      <div class="wt-trap">It cannot be undone from here. The record you merge away is
        gone. What it was holding is not.</div>
    </div>`;

  host.querySelectorAll<HTMLButtonElement>("[data-merge]").forEach((b) =>
    b.addEventListener("click", async () => {
      const drop = b.dataset["merge"]!, keep = b.dataset["into"]!;
      const slot = b.closest(".card")?.querySelector<HTMLElement>("[data-msg]");
      if (!confirm(
        `Move everything from "${b.dataset["name"]}" onto "${b.dataset["keepname"]}" `
        + `and delete the copy?\n\nThis cannot be undone.`)) return;
      b.disabled = true; b.textContent = "Merging…";
      try {
        const sb2 = await db();
        const { data: out, error: err } = await sb2.rpc("merge_customers",
          { p_keep: keep, p_drop: drop });
        if (err) throw new Error(err.message);
        const m = (out ?? {}) as Record<string, number>;
        if (slot) {
          slot.innerHTML = `<p class="msg good">Merged. Moved ${
            ["jobs", "vehicles", "invoices", "messages", "photos", "leads", "tasks"]
              .filter((k) => Number(m[k]) > 0)
              .map((k) => `${m[k]} ${k}`).join(", ") || "nothing — the copy was empty"
          }.</p>`;
        }
        setTimeout(() => void duplicatesPage(role), 1200);
      } catch (e) {
        b.disabled = false; b.textContent = "Merge";
        if (slot) {
          slot.innerHTML = `<p class="msg bad">${
            esc(e instanceof Error ? e.message : String(e))}</p>`;
        }
      }
    }));
}

/** What a record is holding, so nobody has to guess which copy matters. */
function holds(c: Dup): string {
  const bits: string[] = [];
  const add = (n: number | string, word: string) => {
    const v = Number(n);
    if (v > 0) bits.push(`<span><b>${v}</b> ${word}${v === 1 ? "" : "s"}</span>`);
  };
  add(c.jobs, "job");
  add(c.vehicles, shopWords().thing);
  add(c.invoices, "invoice");
  add(c.messages, "message");
  add(c.photos, "photo");
  if (Number(c.money) > 0) {
    bits.push(`<span class="paid"><b>${money(Number(c.money))}</b> collected</span>`);
  }
  return bits.length ? bits.join("") : `<span class="quiet">nothing on it</span>`;
}

/* ── the same work, twice ───────────────────────────────────────────── */

async function drawJobs(host: HTMLElement, role: Role, quotesOnly: boolean): Promise<void> {
  const sb = await db();
  const { data, error } = await sb.rpc("duplicate_jobs", { p_only_quotes: quotesOnly });
  if (error) throw new Error(error.message);
  const pairs = (data ?? []) as Pair[];
  const noun = quotesOnly ? "quote" : "job";

  host.innerHTML = `
    <div class="mtiles">
      <div class="mtile count"><div class="mtile-l">Pairs worth a look</div>
        <div class="mtile-v">${pairs.length}</div>
        <div class="mtile-n">only ever offers to retire a ${noun} with
          <b>nothing at all on it</b></div></div>
    </div>

    ${pairs.length ? pairs.map((p) => `
      <div class="card" data-pair="${esc(p.drop_id)}">
        <div class="card-head">${esc(p.customer)}
          <span class="pill">${p.score} of 15 signals</span></div>

        <div class="duppair">
          <a class="dupside keeper" href="#/job/${esc(p.keep_id)}">
            <div class="dupside-t">Keeping</div>
            <div class="dupside-n">${esc(p.keep_number)}
              <span class="pill s-${esc(p.keep_status)}">${esc(p.keep_status)}</span></div>
            <div class="dupside-d">${esc(p.keep_title || "no description")}</div>
            <div class="dupside-m">${p.keep_total > 0
              ? money(p.keep_total) : "no lines"} &middot; ${esc(ago(p.keep_made))} old</div>
          </a>
          <a class="dupside dropping" href="#/job/${esc(p.drop_id)}">
            <div class="dupside-t">The copy</div>
            <div class="dupside-n">${esc(p.drop_number)}
              <span class="pill s-${esc(p.drop_status)}">${esc(p.drop_status)}</span></div>
            <div class="dupside-d">${esc(p.drop_title || "no description")}</div>
            <div class="dupside-m">nothing on it &middot; ${esc(ago(p.drop_made))} old</div>
          </a>
        </div>

        <div class="dupwhy">
          ${p.reasons.map((r) => `<span>${esc(r)}</span>`).join("")}
        </div>

        <div class="next-do">
          <button class="btn ghost" data-retire="${esc(p.drop_id)}"
            data-keep="${esc(p.keep_id)}" data-num="${esc(p.drop_number)}"
            data-keepnum="${esc(p.keep_number)}">Cancel ${esc(p.drop_number)} as a duplicate</button>
          <a class="btn ghost" href="#/job/${esc(p.drop_id)}">Open it first</a>
        </div>
        <div class="msg-slot" data-msg="${esc(p.drop_id)}"></div>
      </div>`).join("") : `<div class="card"><div class="empty">
        Nothing here that looks like the same ${noun} twice. <span>&#127942;</span></div></div>`}

    <div class="card">
      <div class="card-head">How this list is built, and what it refuses</div>
      <div class="wt-trap">A ${noun} is only ever offered for retirement when it has
        <b>no lines, no invoice, no authorization, no photos and no inspection</b>, and
        is sitting at estimate or booked. Anything with work, money or a signature on it
        is somebody's ${noun} and this page has no opinion about it.</div>
      <div class="wt-trap">Retiring <b>cancels, it does not delete</b>. The number has
        been said to a customer, and a deleted row takes its history with it. Anything
        attached moves to the ${noun} that survives, and both records get a note saying
        what happened.</div>
      <div class="wt-trap">1,774 of this shop's jobs were written in one hour by the
        migration, so for those two the clock proves nothing &mdash; the list says so out
        loud where that applies rather than counting it as evidence.</div>
    </div>`;

  host.querySelectorAll<HTMLButtonElement>("[data-retire]").forEach((b) =>
    b.addEventListener("click", async () => {
      const drop = b.dataset["retire"]!, keep = b.dataset["keep"]!;
      const slot = b.closest(".card")?.querySelector<HTMLElement>("[data-msg]");
      const why = prompt(
        `Cancel ${b.dataset["num"]} as a duplicate of ${b.dataset["keepnum"]}?\n\n`
        + `Why, for the record:`,
        `The same work was already on ${b.dataset["keepnum"]}.`);
      if (why === null) return;
      b.disabled = true; b.textContent = "Cancelling…";
      try {
        const sb2 = await db();
        const { data: out, error: err } = await sb2.rpc("retire_duplicate_job",
          { p_drop: drop, p_keep: keep, p_why: why });
        if (err) throw new Error(err.message);
        const r = (out ?? {}) as { cancelled?: string; moved?: number; already?: boolean };
        if (slot) {
          slot.innerHTML = `<p class="msg good">${
            r.already ? "That one was already cancelled."
              : `${esc(r.cancelled ?? "It")} cancelled${
                  Number(r.moved) > 0 ? `, and ${r.moved} attached ${
                    Number(r.moved) === 1 ? "record" : "records"} moved across` : ""}.`}</p>`;
        }
        setTimeout(() => void duplicatesPage(role), 1200);
      } catch (e) {
        b.disabled = false; b.textContent = `Cancel ${b.dataset["num"]} as a duplicate`;
        if (slot) {
          slot.innerHTML = `<p class="msg bad">${
            esc(e instanceof Error ? e.message : String(e))}</p>`;
        }
      }
    }));
}
